import { useQuery } from "@tanstack/react-query";

import { apiRequest } from "@/lib/api";
import { api } from "@/lib/api-typed";
import { enqueue, isOfflineError } from "@/lib/offline-queue";
import { QueuedOfflineError } from "@/hooks/security/use-security";

import type { Schemas } from "@/types/api";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
export type VisitStatus = "agendada" | "em_curso" | "terminada" | "cancelada" | "nao_compareceu";

export type Visit = Schemas["VisitRead"];

interface ListResponse<T> {
  items: T[];
  total: number;
}

export const VISIT_STATUS_LABELS: Record<VisitStatus, string> = {
  agendada: "Agendada",
  em_curso: "Cá dentro",
  terminada: "Terminada",
  cancelada: "Cancelada",
  nao_compareceu: "Não compareceu",
};

const VISITS_KEY = ["security", "visits"] as const;

/** Esperados hoje — o primeiro ecrã do segurança. */
export const useTodayVisits = () =>
  useQuery({
    queryKey: [...VISITS_KEY, "today"],
    queryFn: () => api.get("/security/visits/today"),
    // A portaria fica sem rede: manter o que já foi lido em vez de esvaziar o
    // ecrã. Sem isto o segurança abria "Hoje" offline e não via ninguém.
    staleTime: 5 * 60_000,
    gcTime: 24 * 60 * 60_000,
  });

export interface VisitListParams {
  status?: VisitStatus;
  /** Um dia só (YYYY-MM-DD). Se vier preenchido, o backend ignora o intervalo. */
  day?: string;
  /** Início do intervalo (YYYY-MM-DD), inclusivo. */
  from_date?: string;
  /** Fim do intervalo (YYYY-MM-DD), inclusivo — apanha o dia inteiro. */
  to_date?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

/**
 * A chave é um OBJECTO, não uma lista posicional: os parâmetros passaram de três
 * para sete e uma lista posicional partia-se (ou colidia em cache) a cada campo
 * novo. Com objecto, o React Query serializa por igualdade estrutural e
 * acrescentar um filtro deixa de ser uma mudança perigosa.
 */
export const useVisits = (params?: VisitListParams, enabled = true) =>
  useQuery({
    enabled,
    queryKey: [...VISITS_KEY, "list", { ...params }],
    queryFn: () => {
      const qs = new URLSearchParams();
      for (const [key, value] of Object.entries(params ?? {})) {
        if (value !== undefined && value !== null && value !== "") qs.set(key, String(value));
      }
      const suffix = qs.toString() ? `?${qs}` : "";
      return apiRequest<ListResponse<Visit>>(`/security/visits${suffix}`);
    },
    // Trocar de dia/semana não deve piscar a lista para vazio: mantém o que já
    // estava enquanto o novo intervalo chega.
    placeholderData: (prev) => prev,
  });

export type VisitCreatePayload = Schemas["VisitCreate"];

export const useCreateVisit = () => {
  return useMutationWithToast({
    mutationFn: (data: VisitCreatePayload) => api.post("/security/visits", { body: data }),
    invalidateQueries: [VISITS_KEY],
  });
};

export const useUpdateVisit = () => {
  return useMutationWithToast({
    mutationFn: ({ id, ...data }: { id: string } & Partial<VisitCreatePayload> & { status?: VisitStatus }) =>
      api.patch("/security/visits/{visit_id}", { path: { visit_id: id }, body: data }),
    invalidateQueries: [VISITS_KEY],
  });
};

/**
 * Check-in / check-out da portaria.
 *
 * `networkMode: "always"` é essencial: por omissão o React Query PAUSA
 * mutations offline e o `mutationFn` nunca corria — o toque do segurança
 * desaparecia sem ir para a fila. Ver `use-security.ts`.
 *
 * `occurred_at` leva a hora REAL do toque: um check-in feito offline às 09:00 e
 * sincronizado às 11:00 não pode ficar registado às 11:00 — o registo de
 * portaria é o documento legal da entrada.
 */
const useCheckAction = (kind: "visit-check-in" | "visit-check-out") => {
  const acao = kind === "visit-check-in" ? "check-in" : "check-out";

  return useMutationWithToast({
    mutationFn: async ({ visit, ...extra }: { visit: Visit; visitor_document?: string; vehicle_plate?: string }) => {
      const body = { occurred_at: new Date().toISOString(), ...extra };
      try {
        return await apiRequest<Visit>(`/security/visits/${visit.id}/${acao}`, { method: "POST", body });
      } catch (error) {
        if (isOfflineError(error)) {
          const verbo = kind === "visit-check-in" ? "Entrada" : "Saída";
          await enqueue(kind, { visit_id: visit.id, ...body }, `${verbo}: ${visit.visitor_name}`);
          throw new QueuedOfflineError();
        }
        throw error;
      }
    },
    invalidateQueries: [["security"]],
    mutationOptions: {
      networkMode: "always"
    },
  });
};

export const useCheckIn = () => useCheckAction("visit-check-in");
export const useCheckOut = () => useCheckAction("visit-check-out");

// ─── Autocomplete ──────────────────────────────────────────────────────────────


export const useHosts = (q: string) =>
  useQuery({
    queryKey: ["security", "hosts", q],
    queryFn: () => api.get("/security/hosts", { query: { q } }),
    staleTime: 5 * 60_000,
  });

/** Valores já usados no passado (empresa, matrícula, documento). */
export const useGateSuggestions = (field: "company" | "plate" | "document" | "person", q: string) =>
  useQuery({
    queryKey: ["security", "suggestions", field, q],
    queryFn: () =>
      api.get("/security/gate-entries/suggestions", { query: { field, q } }),
    enabled: q.trim().length >= 2,
    staleTime: 5 * 60_000,
  });
